/**
 * DescriptionsSection 描述 (S1.4) — the three doc descriptions as 3-up cards
 * with their doc-endpoint captions (報關 / customer docs / vendor PO).
 * All three gate 產品就緒 Product Ready.
 *
 * 0708-J alignment: also hosts the notes trio — 商品備註 (US-183,
 * audience-scoped list; pop-up behaviour deferred), 瑕疵備註 and 商品註解.
 * Notes never gate readiness.
 *
 * Y-S3: hosts 採購單備註 purchaseOrderNote (DD!46 Should) below the 3-up doc
 * cards — flows onto the vendor PO doc; hide-when-empty (detail-page
 * convention for optional fields) and never gates 產品就緒.
 */

import type { CSSProperties } from 'react';
import { Table, Tag, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import { useTranslation } from 'react-i18next';
import TableContainer from '@/components/common/TableContainer';
import { TimestampDisplay } from '@/components/common/TimestampDisplay';
import { radiusTokens } from '@/styles/tokens';
import type { Product, ProductNote, ProductNoteAudience } from '@/types/api/product';
import SectionHeader from './SectionHeader';

const { Text } = Typography;

interface DescriptionCard {
  key: string;
  labelKey: string;
  captionKey: string;
  value?: string;
}

interface DescriptionsSectionProps {
  product: Product;
  onEdit: () => void;
}

export const DescriptionsSection = ({ product, onEdit }: DescriptionsSectionProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();

  const cards: DescriptionCard[] = [
    {
      key: 'sales',
      labelKey: 'products:fields.salesDescription',
      captionKey: 'products:wizard.descriptionsHelp.sales',
      value: product.salesDescription,
    },
    {
      key: 'purchase',
      labelKey: 'products:fields.purchaseDescription',
      captionKey: 'products:wizard.descriptionsHelp.purchase',
      value: product.purchaseDescription,
    },
    {
      key: 'customs',
      labelKey: 'products:fields.customsDescription',
      captionKey: 'products:wizard.descriptionsHelp.customs',
      value: product.customsDescription,
    },
  ];

  const noteColumns: TableProps<ProductNote>['columns'] = [
    {
      title: t('products:notes.audience'),
      dataIndex: 'audience',
      key: 'audience',
      width: 140,
      render: (audience: ProductNoteAudience) => (
        <Tag color="purple">{t(`products:notes.audiences.${audience}`)}</Tag>
      ),
    },
    {
      title: t('products:notes.text'),
      dataIndex: 'text',
      key: 'text',
      render: (value: string) => <Text style={{ whiteSpace: 'pre-wrap' }}>{value}</Text>,
    },
    {
      title: t('products:notes.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date: string) => <TimestampDisplay date={date} showLabel={false} />,
    },
  ];

  const noteCards: { key: string; labelKey: string; value?: string }[] = [
    {
      key: 'damageStockNotes',
      labelKey: 'products:fields.damageStockNotes',
      value: product.damageStockNotes,
    },
    { key: 'itemComment', labelKey: 'products:fields.itemComment', value: product.itemComment },
  ];

  const cardStyle: CSSProperties = {
    backgroundColor: token.colorFillQuaternary,
    borderRadius: radiusTokens.panel,
    padding: token.paddingMD,
    display: 'flex',
    flexDirection: 'column',
    gap: token.marginXS,
  };

  return (
    <div>
      <SectionHeader
        title={t('products:detail.sections.descriptions')}
        subtitle={t('products:detail.descriptionsSubtitle')}
        editLabel={t('common:actions.edit')}
        onEdit={onEdit}
        editDisabled={!product.isActive}
      />
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
        {cards.map((card) => (
          <div key={card.key} style={cardStyle}>
            <Text strong>{t(card.labelKey)}</Text>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t(card.captionKey)}
            </Text>
            <Text style={{ whiteSpace: 'pre-wrap' }}>{card.value?.trim() ? card.value : '-'}</Text>
          </div>
        ))}
      </div>

      {/* DD!46 採購單備註 (Y-S3) — hide-when-empty; edited via the same modal */}
      {product.purchaseOrderNote?.trim() && (
        <div className="tw-mt-4" style={cardStyle}>
          <Text strong>{t('products:fields.purchaseOrderNote')}</Text>
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {t('products:wizard.descriptionsHelp.purchaseOrderNote')}
          </Text>
          <Text style={{ whiteSpace: 'pre-wrap' }}>{product.purchaseOrderNote}</Text>
        </div>
      )}

      {/* 0708-J notes trio — 商品備註 / 瑕疵備註 / 商品註解 */}
      <div className="tw-mt-6">
        <Typography.Title level={5}>{t('products:fields.productNotes')}</Typography.Title>
        <Text
          type="secondary"
          style={{ fontSize: token.fontSizeSM, display: 'block', marginBottom: token.marginSM }}
        >
          {t('products:notes.help')}
        </Text>
        <TableContainer>
          <Table<ProductNote>
            rowKey="id"
            size="small"
            dataSource={product.productNotes ?? []}
            columns={noteColumns}
            pagination={false}
            locale={{ emptyText: t('products:notes.empty') }}
          />
        </TableContainer>
      </div>

      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mt-6">
        {noteCards.map((card) => (
          <div key={card.key} style={cardStyle}>
            <Text strong>{t(card.labelKey)}</Text>
            <Text style={{ whiteSpace: 'pre-wrap' }}>{card.value?.trim() ? card.value : '-'}</Text>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DescriptionsSection;
